import React from 'react'
import styled from '@emotion/styled'
import { createDraggable, type Draggable } from 'animejs'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { spacing } from '@mobi/component-library/Foundations'
import { hexColors } from '@mobi/settings/hexColors'
import { QUICKBET_MODAL_ID } from '@core/Areas/Quickbet/constants'

export const SelectionOverflow: React.FC<{
  children: React.ReactNode
  shouldRestrictHeight: boolean
}> = ({ children, shouldRestrictHeight }) => {
  if (!shouldRestrictHeight) {
    return children
  }

  return <OverflowWrapper>{children}</OverflowWrapper>
}

// ===============
// Local Component
// ===============

const OverflowWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const overflowWrapperElRef = React.useRef<HTMLDivElement>(null)
  const overflowContentElRef = React.useRef<HTMLDivElement>(null)

  const draggableRef = React.useRef<Draggable | null>(null)

  // ============================
  // Main Overflow Content Set Up
  // ============================
  React.useEffect(() => {
    if (!overflowContentElRef.current || !overflowWrapperElRef.current) return

    const { height: wrapperHeight } = overflowWrapperElRef.current.getBoundingClientRect()
    const { height: contentHeight } = overflowContentElRef.current.getBoundingClientRect()

    const bottomScrollPx = -(contentHeight - wrapperHeight)

    overflowWrapperElRef.current.setAttribute('data-js-qb-selection-overflow-bottom', 'true')

    const overflowDraggable = createDraggable(overflowContentElRef.current, {
      x: false,
      container: [bottomScrollPx, 0, 0, 0],
      containerFriction: 0.95,
      onUpdate: draggable => {
        const el = overflowWrapperElRef.current
        const bottomCurrentScrollPx = draggable.coords[3]
        const bottomScrollDiffPx = bottomScrollPx - bottomCurrentScrollPx

        if (!el) return

        const shouldDisplayTopGradient = bottomCurrentScrollPx <= -2
        el.setAttribute('data-js-qb-selection-overflow-top', `${shouldDisplayTopGradient}`)

        const shouldDisplayBottomGradient = bottomScrollDiffPx <= -2
        el.setAttribute('data-js-qb-selection-overflow-bottom', `${shouldDisplayBottomGradient}`)

        if (bottomCurrentScrollPx > 10) {
          document.getElementById(QUICKBET_MODAL_ID)?.scrollBy(0, -4)
        }
        if (bottomScrollDiffPx > 10) {
          document.getElementById(QUICKBET_MODAL_ID)?.scrollBy(0, 4)
        }
      },
    })
    draggableRef.current = overflowDraggable

    return () => {
      overflowDraggable.revert()
    }
  }, [])

  // ================================
  // Scroll Wheel Listener (Optional)
  // ================================
  React.useEffect(() => {
    const shouldEnableWheelListener = !isReactNativeApp && matchMedia('(pointer:fine)').matches
    if (!shouldEnableWheelListener || !overflowWrapperElRef.current) return

    const abortController = new AbortController()
    overflowWrapperElRef.current.addEventListener(
      'wheel',
      event => {
        const draggable = draggableRef.current
        if (!draggable) return

        // Wheel Scroll Up
        if (event.deltaY < 0) {
          const hasReachedBoundary = draggable.progressY > 1
          if (hasReachedBoundary) return
          draggable.setY(draggable.y + 1)
          return
        }

        // Wheel Scroll Down
        if (event.deltaY > 0) {
          const hasReachedBoundary = draggable.progressY < 0
          if (hasReachedBoundary) return
          draggable.setY(draggable.y - 1)
          return
        }
      },
      { signal: abortController.signal }
    )
    return () => abortController.abort()
  }, [])

  return (
    <WrapperStyled ref={overflowWrapperElRef}>
      <div ref={overflowContentElRef}>{children}</div>
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  position: 'relative',
  maxHeight: '28rem',
  overflowY: 'clip',

  '::before, ::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 1,
    height: '5rem',
    marginInline: `-${spacing.sm}`,
    pointerEvents: 'none',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },

  '&[data-js-qb-selection-overflow-top]::before': {
    top: 0,
    background: `linear-gradient(180deg, ${hexColors.blueDiamond} 0%, rgba(82, 41, 107, 0.00) 100%)`,
  },
  '&[data-js-qb-selection-overflow-bottom]::after': {
    bottom: 0,
    background: `linear-gradient(180deg, rgba(82, 41, 107, 0.00) 0%, ${hexColors.blueDiamond} 100%)`,
  },

  '&[data-js-qb-selection-overflow-top="true"]::before, ': {
    opacity: 1,
  },
  '&[data-js-qb-selection-overflow-bottom="true"]::after': {
    opacity: 1,
  },
})
